import { useFormContext } from "react-hook-form";
import styled from "@emotion/styled";
import BasicButton from "@components/button/BasicButton";
import CustomTextInput from "@components/input/CustomTextInput";
import FormTitle from "@pages/join/_components/FormTitle";
import { useEffect, useState } from "react";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import { IFormInput } from "@pages/findpassword/FindPasswordPage";
import { useNavigate, useSearchParams } from "react-router-dom";
import useJoinStepStore from "@store/join/useJoinStepStore";
import { useApiRequest } from "@utils/ApiRequest";
import { useMutation, useQuery } from "@tanstack/react-query";

interface PasswordValidationProps {
  title: string;
  fieldName: keyof IFormInput;
  fieldConfirmName: keyof IFormInput;
  modalMessage?: string;
  subMessage?: string;
  type: "signup" | "resetPassword";
}

// 공용 비밀번호 검증 컴포넌트
export default function PasswordValidationComponent({
  title,
  fieldName,
  fieldConfirmName,
  modalMessage = "",
  subMessage = "",
  type,
}: PasswordValidationProps) {
  const {
    watch,
    setValue,
    formState: { dirtyFields, errors },
    setError,
  } = useFormContext<IFormInput>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emailModalOpen, setIsEmailModalOpen] = useState(false);
  // 유효하지 않은 코드 모달
  const [isVerificationCodeError, setIsVerificationCodeError] = useState(false);

  const [password, passwordConfirm] = watch([fieldName, fieldConfirmName]);
  const [searchParams] = useSearchParams();
  const verificationCode = searchParams.get("verificationCode");
  // 로컬 테스트
  // const verificationCode = "1736839558648";

  useEffect(() => {
    if (password && passwordConfirm && password !== passwordConfirm) {
      setError(fieldConfirmName, {});
    }
  }, [password, passwordConfirm, fieldConfirmName, setError]);

  const navigate = useNavigate();
  const { increaseStep } = useJoinStepStore();

  const { getVerifyEmail, getFindPassword, patchFindPassword } =
    useApiRequest();

  // 메일인증 만료여부 체크
  const { data: verifiedEmail, isError } = useQuery({
    queryKey: ["verifyEmail", verificationCode],
    queryFn: () => getVerifyEmail(verificationCode as string),
    // 인증코드 없으면 실행 X
    enabled: !!verificationCode && type === "signup",
  });

  // 비밀번호 찾기 인증 만료여부 체크
  const { data: findPasswordEmailVerification, isError: findPasswordError } =
    useQuery({
      queryKey: ["findPassword", verificationCode],
      queryFn: () => getFindPassword(verificationCode as string),
      enabled: !!verificationCode && type === "resetPassword",
    });

  // 비밀번호 찾기 변경 submit
  const { mutate: patchPassword } = useMutation({
    mutationFn: (data: { member_id: string; password: string }) => {
      return patchFindPassword(data);
    },
    onSuccess: () => {
      setIsModalOpen(true);
    },
  });

  // 유효하지 않은 코드인 경우
  useEffect(() => {
    if (!verificationCode) {
      setIsVerificationCodeError(true);
    }
  }, [verificationCode]);

  useEffect(() => {
    if (verifiedEmail) {
      setValue("login_id", verifiedEmail.email);
    } else if (findPasswordEmailVerification) {
      setValue("member_id", findPasswordEmailVerification.member_id);
    }
  }, [
    findPasswordEmailVerification,
    setValue,
    fieldName,
    verifiedEmail,
    verificationCode,
  ]);

  // 모달 오픈 상태를 isError와 연결
  useEffect(() => {
    setIsEmailModalOpen(isError || findPasswordError);
  }, [isError, findPasswordError]);

  // 비밀번호 찾기 회원가입 로직 구분
  const valid = async () => {
    if (type === "resetPassword") {
      patchPassword({
        member_id: findPasswordEmailVerification?.member_id,
        password: watch(fieldConfirmName),
      });
    } else if (type === "signup") {
      increaseStep();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      valid();
    }
  };

  return (
    <>
      <ContentWrapper onKeyDown={handleKeyDown}>
        <FormTitle>{title}</FormTitle>

        <InputWrapper>
          <CustomTextInput
            type="password"
            width="100%"
            placeholder="비밀번호 입력"
            message="영문 + 숫자 포함 8-20자 이내"
            name={fieldName}
            isPasswordField={true} // 문구 view 설정
            success={!!dirtyFields[fieldName] && !errors[fieldName]}
          />
          <CustomTextInput
            type="password"
            width="100%"
            placeholder="비밀번호 재확인"
            message="비밀번호 재확인"
            name={fieldConfirmName}
            isPasswordField={true}
            success={
              !!dirtyFields[fieldConfirmName] && !errors[fieldConfirmName]
            }
          />
        </InputWrapper>

        <ButtonWrapper>
          <BasicButton
            title="다음"
            height="5.2rem"
            width="100%"
            padding="1.6rem 1.2rem"
            disabled={!!errors[fieldName] || !!errors[fieldConfirmName]}
            onClick={valid}
          />
        </ButtonWrapper>
      </ContentWrapper>

      {/* 비밀번호 찾기 submit 모달 */}
      {modalMessage && (
        <BasicModal
          open={isModalOpen}
          setOpen={setIsModalOpen}
          activeEvent={() => {
            setIsModalOpen(false);
            navigate("/login");
          }}
          activeName="확인"
          notice={true}
          padding="2rem">
          <ModalContent
            mainMessage={modalMessage}
            subMessage={subMessage ?? ""}
          />
        </BasicModal>
      )}

      {/* 메일 인증 만료 모달 */}
      <BasicModal
        open={emailModalOpen}
        setOpen={setIsEmailModalOpen}
        activeEvent={() => {
          setIsEmailModalOpen(false);
          navigate("/");
        }}
        activeName="확인"
        notice={true}
        padding="2rem">
        <ModalContent
          mainMessage="유효기간이 만료되었습니다."
          subMessage="다시 인증해주세요."
        />
      </BasicModal>

      <BasicModal
        open={isVerificationCodeError}
        setOpen={setIsVerificationCodeError}
        activeEvent={() => {
          setIsVerificationCodeError(false);
          navigate("/");
        }}
        activeName="확인"
        notice={true}
        padding="2rem"
        // maskClosable={false}
      >
        <ModalContent
          mainMessage="유효하지 않은 코드입니다."
          subMessage="다시 인증해주세요."
        />
      </BasicModal>
    </>
  );
}

// 스타일 정의
const ContentWrapper = styled.div({
  // height: "100dvh - 4.8rem",
  display: "flex",
  position: "relative",
  flexDirection: "column",
  justifyContent: "flex-start",
  paddingBottom: "2.4rem",
  minHeight: "calc(100dvh - 5.6rem)",
});

const InputWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: "1.8rem",
  flexGrow: 1,
});

const ButtonWrapper = styled.div({
  position: "absolute",
  width: "100%",
  bottom: "4.8rem",
});
